import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DaySlots } from '@/data/mock';
import type { BookingSelectedTime } from '@/types/booking-flow';
import type { BookingAvailabilityResponseDto } from '@/types/dto/BookingAvailabilityResponse.dto';

interface UseBookingAvailabilityStateParams {
  availabilityEnabled: boolean;
  onClearSelectedTime: () => void;
  selectedProfessionalId: string;
  selectedServices: string[];
  selectedTime: BookingSelectedTime | null;
}

export function useBookingAvailabilityState({
  availabilityEnabled,
  onClearSelectedTime,
  selectedProfessionalId,
  selectedServices,
  selectedTime,
}: UseBookingAvailabilityStateParams) {
  const [availabilityCursor, setAvailabilityCursor] = useState<string | null>(null);
  const [availabilityDays, setAvailabilityDays] = useState<DaySlots[]>([]);
  const availabilityDaysRef = useRef<DaySlots[]>([]);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<Date | undefined>(undefined);
  const [previousAvailabilityEnabled, setPreviousAvailabilityEnabled] = useState(availabilityEnabled);
  const [previousSelectionKey, setPreviousSelectionKey] = useState('');
  const selectedServicesKey = useMemo(() => selectedServices.toSorted().join(','), [selectedServices]);
  const selectionKey = `${selectedProfessionalId}:${selectedServicesKey}`;
  const today = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return currentDate;
  }, []);

  if (selectionKey !== previousSelectionKey) {
    setPreviousSelectionKey(selectionKey);
    setAvailabilityCursor(null);
    setAvailabilityDays([]);
    setSelectedAvailabilityDate(undefined);
    onClearSelectedTime();
  }

  if (availabilityEnabled !== previousAvailabilityEnabled) {
    setPreviousAvailabilityEnabled(availabilityEnabled);

    if (!availabilityEnabled) {
      setAvailabilityCursor(null);
      setAvailabilityDays([]);
    }
  }

  useLayoutEffect(() => {
    availabilityDaysRef.current = availabilityDays;
  }, [availabilityDays]);

  const mapAvailabilityDays = useCallback(
    (days: DaySlots[], cursor: string | null) => {
      const startDate = cursor ? new Date(cursor) : new Date(today);
      startDate.setHours(0, 0, 0, 0);

      return days.map((day, index) => {
        const dayDate = new Date(startDate);
        dayDate.setDate(startDate.getDate() + index);
        dayDate.setHours(0, 0, 0, 0);

        return {
          ...day,
          isoDate: dayDate.toISOString(),
        };
      });
    },
    [today],
  );

  const syncAvailability = useCallback(
    (availability?: BookingAvailabilityResponseDto, cursor: string | null = null) => {
      if (!availability) {
        return;
      }

      const mappedDays = mapAvailabilityDays(availability.data, cursor);

      const nextDays = new Map(
        (cursor === null ? [] : availabilityDaysRef.current).map((day) => [day.isoDate ?? day.date, day]),
      );
      mappedDays.forEach((day) => nextDays.set(day.isoDate ?? day.date, day));
      const nextAvailabilityDays = Array.from(nextDays.values());
      availabilityDaysRef.current = nextAvailabilityDays;
      setAvailabilityDays(nextAvailabilityDays);
      if (
        selectedTime &&
        !nextAvailabilityDays.some(
          (day) =>
            day.date === selectedTime.date &&
            day.slots.some((slot) => slot.time === selectedTime.time && slot.available),
        )
      ) {
        onClearSelectedTime();
      }
    },
    [mapAvailabilityDays, onClearSelectedTime, selectedTime],
  );

  const handleAvailabilityDateSelect = useCallback(
    (date?: Date) => {
      if (!date) {
        return;
      }

      const nextDate = new Date(date);
      nextDate.setHours(0, 0, 0, 0);

      setSelectedAvailabilityDate(nextDate);
      onClearSelectedTime();
      setAvailabilityDays([]);
      availabilityDaysRef.current = [];
      setAvailabilityCursor(nextDate.toISOString());
    },
    [onClearSelectedTime],
  );

  const handleLoadMoreAvailability = useCallback((nextCursor: string | null, isFetchingAvailability: boolean) => {
    if (nextCursor && !isFetchingAvailability) {
      setAvailabilityCursor(nextCursor);
    }
  }, []);

  return {
    availabilityCursor,
    availabilityDays,
    handleAvailabilityDateSelect,
    handleLoadMoreAvailability,
    selectedAvailabilityDate,
    syncAvailability,
    today,
  };
}
